/**
 * Kono internal: the quote email the customer gets for this RFQ. After
 * "Generate quote & prepare email" (or an operator's "Convert to quote",
 * which emails nothing by itself) it sits here unsent: the operator reads it
 * as the customer will, edits subject or body if needed, opens the quote page
 * exactly as the customer sees it, and presses Send. Nothing goes to the
 * customer until then. Edits are remembered: the next draft is written the
 * operators' way.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CustomerEmailState,
  HandSentQuote,
  PreparedCustomerEmail,
  QuoteLineOffer,
  RfqCustomerEmail,
  fetchRfqCustomerEmail,
  fetchRfqCustomerEmails,
  sendRfqCustomerEmail,
} from "./api";
import { trackingSummary } from "./CustomerEmailsPanel";

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "");

const TRACK_TONE: Record<ReturnType<typeof trackingSummary>["tone"], string> = {
  none: "bg-gray-100 text-gray-700",
  opened: "bg-blue-100 text-blue-800",
  clicked: "bg-green-100 text-green-800",
  bounced: "bg-red-100 text-red-800",
};

export const CustomerEmailPanel = ({
  rfqId,
  quoteId,
  refreshKey,
  onError,
  onState,
}: {
  rfqId: string;
  quoteId: string;
  refreshKey: number;
  onError: (message: string) => void;
  /** Tells the workspace the send state, for the last step / next step card. */
  onState?: (state: CustomerEmailState) => void;
}) => {
  const [email, setEmail] = useState<PreparedCustomerEmail | null>(null);
  const [state, setState] = useState<CustomerEmailState | null>(null);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  const [basis, setBasis] = useState<{ basis: string; examplesUsed: number } | null>(null);
  const [offers, setOffers] = useState<QuoteLineOffer[]>([]);
  const [learned, setLearned] = useState(false);
  /** Plain sentences from the server about whether and how the customer got this quote. */
  const [facts, setFacts] = useState<string[]>([]);
  const [handSent, setHandSent] = useState<HandSentQuote | null>(null);
  /** Extra Cc addresses the operator types, comma-separated; added to the copies the system sends. */
  const [extraCc, setExtraCc] = useState("");
  /** The tracked record of the sent quote email: opened, clicked, bounced. */
  const [tracked, setTracked] = useState<RfqCustomerEmail | null>(null);
  /** Sent email reopened by the operator to send it once more. */
  const [resending, setResending] = useState(false);

  const extraCcList = extraCc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  const badCc = extraCcList.filter((e) => !/^[^\s@<>]+@[^\s@<>]+\.[a-z]{2,}$/i.test(e));

  const load = useCallback(async () => {
    try {
      const r = await fetchRfqCustomerEmail(rfqId);
      setEmail(r.email);
      setState(r.state);
      onState?.(r.state);
      setSubject(r.email.subject);
      setText(r.email.text);
      setBasis(r.basis ? { basis: r.basis, examplesUsed: r.examplesUsed || 0 } : null);
      setOffers(r.offers || []);
      setFacts(r.facts || []);
      setHandSent(r.handSent || null);
      if (r.email.sentAt || r.state === "already-sent-automatically") {
        try {
          const all = await fetchRfqCustomerEmails(rfqId);
          const quoteEmails = all.emails.filter((e) => e.status === "sent" && e.kind === "quote-ready");
          setTracked(quoteEmails.sort((a, b) => (b.sentAt || "").localeCompare(a.sentAt || ""))[0] || null);
        } catch {
          setTracked(null);
        }
      } else {
        setTracked(null);
      }
    } catch {
      // The quote block above still works without this panel.
      setEmail(null);
    }
  }, [rfqId, onState]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (!email || !state) return null;

  const unsent = state === "prepared" || state === "draft";
  const factsBlock = facts.length > 0 && (
    <details className="mt-2 rounded border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700" open={unsent || state === "sent-from-mailbox"}>
      <summary className="cursor-pointer font-medium text-gray-800">
        {state === "sent"
          ? "How the customer got this quote"
          : state === "sent-from-mailbox"
            ? `Sent by hand from ${handSent?.mailbox || "a BDR mailbox"}, not through the platform`
            : state === "already-sent-automatically"
              ? "How the customer got this quote"
              : "Why this says not sent: what the platform knows"}
      </summary>
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        {facts.map((f, i) => (
          <li key={i}>{f}</li>
        ))}
      </ul>
    </details>
  );
  const differing = offers.filter((o) => o.differences.length);
  const resends = email.resends || [];
  const lastResend = resends[resends.length - 1] || null;
  /** The sent email is folded to one line until the operator reopens it. */
  const collapsed = !unsent && !resending;
  /** Yellow box, editable, Send button: an unsent email or a sent one reopened to send again. */
  const open = unsent || resending;

  const send = async () => {
    if (badCc.length) {
      onError(`Cc: "${badCc[0]}" is not an email address.`);
      return;
    }
    const allCc = [...email.cc, ...extraCcList];
    const again = !unsent;
    if (!window.confirm(`${again ? "Send this email AGAIN" : "Send this email"} to ${email.to}${allCc.length ? ` (cc ${allCc.join(", ")})` : ""}?`)) return;
    setSending(true);
    try {
      const r = await sendRfqCustomerEmail(rfqId, { subject, text, ...(extraCcList.length ? { cc: extraCcList.join(", ") } : {}), ...(again ? { again: true } : {}) });
      setLearned(Boolean(r.learned));
      setExtraCc("");
      await load();
      setEditing(false);
      setResending(false);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not send the email.");
    } finally {
      setSending(false);
    }
  };


  // The body is link markup ([label](url)) with !!stressed!! passages; show it
  // as the customer reads it: label (url), stressed text bold and underlined.
  const renderPlain = (body: string) =>
    body
      .replace(/<a [^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>/g, "$2 ($1)")
      .split(/(!![^!\n][^\n]*?!!|\[[^\]\n]+\]\(https?:\/\/[^\s)]+\))/g)
      .map((part, i) => {
        const stressed = part.match(/^!!([^\n]*?)!!$/);
        if (stressed) return <strong key={i} className="underline">{stressed[1]}</strong>;
        const link = part.match(/^\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)$/);
        if (link) return <span key={i}>{link[1]} <span className="text-gray-500">({link[2]})</span></span>;
        return <span key={i}>{part}</span>;
      });

  const sentBadge = (
    <>
      {state === "sent" && <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-green-800">sent {when(email.sentAt)} by {email.sentBy}</span>}
      {state === "already-sent-automatically" && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700">sent automatically when the quote was generated</span>}
      {state === "sent-from-mailbox" && handSent && (
        <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-green-800" title={`"${handSent.subject}" to ${handSent.to}, found in the Sent folder of ${handSent.mailbox}`}>
          sent by hand {when(handSent.at)} from {handSent.mailbox}
        </span>
      )}
      {lastResend && (
        <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-green-800" title={resends.map((r) => `${when(r.at)} by ${r.by}`).join("\n")}>
          sent again {when(lastResend.at)} by {lastResend.by}{resends.length > 1 ? ` (${resends.length} times)` : ""}
        </span>
      )}
    </>
  );
  const customerDid = (
    <>
      <span className="text-gray-500">What the customer did: </span>
      {tracked ? (
        <span className={`rounded px-1.5 py-0.5 font-medium ${TRACK_TONE[trackingSummary(tracked).tone]}`}>{trackingSummary(tracked).text}</span>
      ) : (
        <span className="text-gray-500">not tracked (sent before open and click tracking existed)</span>
      )}
    </>
  );

  // Sent: one line (when, by whom, what the customer did) and the way to
  // open the quote or send the same email once more.
  if (collapsed) {
    return (
      <div className="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold text-[#00062e]">
            Customer email
            <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">Kono internal</span>
            {sentBadge}
          </h3>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/quotes/${quoteId}`}
              target="_blank"
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Open the quote as the customer sees it
            </Link>
            <button
              type="button"
              className="rounded-lg border border-teal-600 bg-white px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50"
              title="Open the email the customer received and send it to them one more time"
              onClick={() => setResending(true)}
            >
              Send the original email again
            </button>
          </div>
        </div>
        <div className="mt-1 text-xs">
          {state === "sent-from-mailbox" ? (
            <span className="text-gray-600">Sent from the operator's own mailbox: opens and clicks are not tracked, and the platform copy below is the draft, not the email that went out.</span>
          ) : (
            customerDid
          )}
          {tracked && <span className="ml-2 text-gray-500">Follow-ups are suggested below under "Customer emails" when it is time.</span>}
          {learned && <span className="ml-2 text-indigo-700">Your edits were saved for the next drafts.</span>}
        </div>
        {factsBlock}
      </div>
    );
  }

  return (
    <div className={`mt-4 rounded-xl border p-4 text-sm ${open ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-[#00062e]">
          Customer email
          <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">Kono internal</span>
          {unsent && <span className="ml-2 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900" title="No send of this quote version is recorded on the platform or seen in the parsed mailboxes. The details are listed below.">not sent from the platform</span>}
          {sentBadge}
        </h3>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/quotes/${quoteId}`}
            target="_blank"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Open the quote as the customer sees it
          </Link>
          {resending && (
            <button
              type="button"
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setResending(false);
                setEditing(false);
                setSubject(email.subject);
                setText(email.text);
                setExtraCc("");
              }}
            >
              Cancel
            </button>
          )}
          <button type="button" className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50" onClick={() => setEditing((v) => !v)}>
            {editing ? "Done editing" : "Edit"}
          </button>
          <button type="button" disabled={sending} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50" onClick={() => void send()}>
            {sending ? "Sending..." : resending ? "Send again to customer" : "Send to customer"}
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-600">
        {unsent
          ? "This is exactly what the customer will receive if you send it from here. The platform has no record of this version going out (details below); if you already emailed it from your own mailbox, nothing more is needed. Your edits are remembered and shape the next drafts."
          : "This is the email the customer already received. Nothing is sent until you press Send again; edit it first if you like (the edits are not kept for later drafts)."}
        {basis?.basis === "edits" && unsent && (
          <span className="ml-1 text-indigo-700">Written the team's way from {basis.examplesUsed} earlier edit{basis.examplesUsed === 1 ? "" : "s"}.</span>
        )}
      </p>
      {!unsent && <div className="mt-2 text-xs">{customerDid}</div>}
      {factsBlock}
      {differing.length > 0 && (
        <div className="mt-2 rounded border border-amber-300 bg-white px-3 py-2 text-xs text-gray-800">
          <div className="font-semibold text-amber-900">
            {differing.length} of {offers.length} quoted item{offers.length === 1 ? "" : "s"} differ{differing.length === 1 ? "s" : ""} from what was requested. The email spells this out; the differences are bold and underlined for the customer.
          </div>
          <ul className="mt-1 space-y-1">
            {differing.map((o, i) => (
              <li key={i}>
                <span className="text-gray-500">Asked:</span> {o.requested} <span className="text-gray-500">Offered:</span> {o.offered}{" "}
                {o.differences.map((d, j) => (
                  <strong key={j} className="underline">{d}{j < o.differences.length - 1 ? "; " : ""}</strong>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-gray-700">
        <dt className="text-gray-500">From</dt>
        <dd>
          {email.from.name} &lt;{email.from.email}&gt;
        </dd>
        <dt className="text-gray-500">To</dt>
        <dd>{email.to}</dd>
        {(email.cc.length > 0 || open) && (
          <>
            <dt className="text-gray-500">Cc</dt>
            <dd>
              {email.cc.length > 0 && <span>{email.cc.join(", ")}</span>}
              {open && (
                <input
                  className={`mt-1 w-full rounded border px-2 py-1 text-xs ${badCc.length ? "border-red-400" : "border-gray-300"}`}
                  placeholder="Add addresses to copy, separated by commas (e.g. buyer2@company.com, manager@company.com)"
                  title="Extra people to copy on this email; the copies the system already sends stay"
                  value={extraCc}
                  onChange={(e) => setExtraCc(e.target.value)}
                />
              )}
              {badCc.length > 0 && <div className="mt-0.5 text-[11px] text-red-700">"{badCc[0]}" is not an email address.</div>}
            </dd>
          </>
        )}
        <dt className="text-gray-500">Subject</dt>
        <dd>
          {editing ? (
            <input className="w-full rounded border border-gray-300 px-2 py-1 text-xs" value={subject} onChange={(e) => setSubject(e.target.value)} />
          ) : (
            <span className="font-medium text-gray-900">{subject}</span>
          )}
        </dd>
      </dl>
      {editing ? (
        <textarea className="mt-3 w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs" rows={12} value={text} onChange={(e) => setText(e.target.value)} />
      ) : (
        <pre className="mt-3 whitespace-pre-wrap rounded border border-gray-200 bg-white px-3 py-2 font-sans text-xs text-gray-800">{renderPlain(text)}</pre>
      )}
    </div>
  );
};
